import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Menu,
  MenuItem,
  Paper,
  ThemeProvider as MUIThemeProvider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SortIcon from '@mui/icons-material/Sort';
import { lightTheme } from '../../theme';

const ProjectSidebar = ({ projects, selectedProject, onProjectSelect, canCreateProject, isSettingsView, sortBy, onSortChange }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Filter out archived projects
  const activeProjects = projects.filter(project => project.status !== 'archived');

  const handleNewProject = () => {
    setOpen(false);
    navigate('/projects/new');
  };

  const handleSortClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setAnchorEl(null);
  };

  const handleSortSelect = (value) => {
    onSortChange(value);
    handleSortClose();
  };

  return (
    <MUIThemeProvider theme={lightTheme}>
      <Paper
        elevation={3}
        sx={{
          width: 250,
          flexShrink: 0,
          bgcolor: lightTheme.palette.primary.main,
          color: lightTheme.palette.primary.contrastText,
          overflow: 'auto',
          borderRadius: 0,
          borderTop: 0,
          height: '100%',
          position: 'relative',
          mt: '-1px',
          '& .MuiListItemButton-root': {
            '&.Mui-selected': {
              bgcolor: lightTheme.palette.primary.dark,
              '&:hover': {
                bgcolor: lightTheme.palette.primary.dark,
              },
            },
            '&:hover': {
              bgcolor: `${lightTheme.palette.primary.dark}80`,
            },
          },
          '& .MuiTypography-root': {
            color: lightTheme.palette.primary.contrastText,
          },
          '& .MuiListItemText-secondary': {
            color: `${lightTheme.palette.primary.contrastText}B3`,
          },
          '& .MuiIconButton-root': {
            color: lightTheme.palette.primary.contrastText,
          },
          '& .MuiDivider-root': {
             borderColor: 'rgba(255, 255, 255, 0.12)'
          }
        }}
      >
        <Box 
          sx={{ 
            p: 2, 
            display: 'flex',                   
            alignItems: 'center',             
            justifyContent: 'space-between',  
            borderBottom: 1, 
            borderColor: 'rgba(255, 255, 255, 0.12)' 
          }}
        >
          <Typography variant="h6">
            {isSettingsView ? 'Project Instellingen' : 'Projecten'}
          </Typography>
          <Box>
            <IconButton onClick={handleSortClick} size="small">
              <SortIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleSortClose}
            >
              <MenuItem onClick={() => handleSortSelect('name-asc')}>Naam (A-Z)</MenuItem>
              <MenuItem onClick={() => handleSortSelect('name-desc')}>Naam (Z-A)</MenuItem>
              <MenuItem onClick={() => handleSortSelect('created-desc')}>Nieuwste eerst</MenuItem>
              <MenuItem onClick={() => handleSortSelect('created-asc')}>Oudste eerst</MenuItem>
            </Menu>
          </Box>
          {canCreateProject && (
            <IconButton
              onClick={() => setOpen(true)}
              size="small"
            >
              <AddIcon />
            </IconButton>
          )}
        </Box>
        <List>
          {activeProjects.map((project) => (
            <ListItem key={project.id} disablePadding>
              <ListItemButton
                selected={selectedProject?.id === project.id}
                onClick={() => onProjectSelect(project)}
              >
                <ListItemText
                  primary={project.name}
                  secondary={isSettingsView ? 'Instellingen' : null}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Dialog open={open} onClose={() => setOpen(false)}>
          <DialogTitle>Nieuw Project</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Wil je een nieuw project aanmaken?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Annuleren</Button>
            <Button onClick={handleNewProject} variant="contained">
              Doorgaan
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </MUIThemeProvider>
  );
};

export default ProjectSidebar;
